'use client';

import { useEffect, useId, useRef, useState } from 'react';

type TOCItem = { id: string; text: string; tag: string };

const TINT = 'color-mix(in srgb, var(--color-t-primary) 8%, transparent)';

/**
 * Sticky "Table of Contents" with scroll-spy: the link of the heading that last
 * crossed the top band of the viewport is highlighted and carries
 * aria-current="location". Used by help articles (defaults keep their look)
 * and the "Article with Contents" block (custom title + Back to top).
 *
 * The title is a plain element that names the <nav> — not a heading, so it
 * never breaks the page's heading outline.
 */
export function TableOfContents({
  headings,
  title = 'Table of Contents',
  showBackToTop = false,
  background = 'white',
}: {
  headings: TOCItem[];
  title?: string;
  /** Adds a "Back to top" link under the list. */
  showBackToTop?: boolean;
  /** Card background (CSS colour / theme token). */
  background?: string;
}) {
  const [activeId, setActiveId] = useState<string>('');
  const titleId = useId();

  useEffect(() => {
    // Older browsers / non-DOM environments: the links still work, just no spy.
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-100px 0px -75% 0px' },
    );

    for (const { id } of headings) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [headings]);

  if (!headings.length) return null;

  return (
    <div className="rounded-xl border p-5" style={{ backgroundColor: background, borderColor: TINT }}>
      <p
        id={titleId}
        className="text-xs font-bold uppercase tracking-wider mb-4 pb-3"
        style={{
          color: 'var(--color-t-body)',
          opacity: 0.5,
          borderBottom: `1px solid ${TINT}`,
        }}
      >
        {title}
      </p>
      <nav aria-labelledby={titleId}>
        <ul className="space-y-0.5">
          {headings.map((h) => {
            const isActive = activeId === h.id;
            return (
              <li key={h.id}>
                <a
                  href={`#${h.id}`}
                  aria-current={isActive ? 'location' : undefined}
                  onClick={() => setActiveId(h.id)}
                  className="block text-[13px] leading-snug py-1.5 transition-colors"
                  style={{
                    paddingLeft: h.tag === 'h3' ? '1.25rem' : '0.625rem',
                    borderLeft: isActive
                      ? '2px solid var(--color-t-cta)'
                      : '2px solid transparent',
                    color: isActive ? 'var(--color-t-cta)' : 'var(--color-t-body)',
                    fontWeight: isActive ? 600 : 400,
                    opacity: isActive ? 1 : 0.65,
                  }}
                >
                  {h.text}
                </a>
              </li>
            );
          })}
        </ul>
        {showBackToTop && (
          // "#top" scrolls to the top of the document (HTML spec) — no JS needed.
          <a
            href="#top"
            data-testid="toc-back-to-top"
            className="mt-4 pt-3 flex items-center gap-1.5 text-xs font-semibold hover:underline"
            style={{ color: 'var(--color-t-primary-dark)', borderTop: `1px solid ${TINT}` }}
          >
            <svg aria-hidden="true" focusable="false" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m-7 7 7-7 7 7" />
            </svg>
            Back to top
          </a>
        )}
      </nav>
    </div>
  );
}

/**
 * The same contents as a collapsible menu (below lg). A native <details>, so it
 * opens and closes without JS; choosing a link closes it first, so the jump
 * lands on the heading of the collapsed layout.
 */
export function CollapsibleTableOfContents({
  headings,
  title = 'In this article',
}: {
  headings: TOCItem[];
  title?: string;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  if (!headings.length) return null;

  const close = () => {
    if (ref.current) ref.current.open = false;
  };

  return (
    <details
      ref={ref}
      className="group rounded-xl border"
      style={{ backgroundColor: 'var(--color-t-surface)', borderColor: TINT }}
    >
      <summary
        className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 text-sm font-semibold [&::-webkit-details-marker]:hidden"
        style={{ color: 'var(--color-t-heading)' }}
      >
        <span>{title}</span>
        <svg
          aria-hidden="true"
          focusable="false"
          className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <nav aria-label={title} className="px-5 pb-4">
        <ul className="space-y-0.5 pt-3" style={{ borderTop: `1px solid ${TINT}` }}>
          {headings.map((h) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                onClick={close}
                className="block py-2 text-sm leading-snug hover:underline"
                style={{
                  paddingLeft: h.tag === 'h3' ? '1rem' : 0,
                  color: 'var(--color-t-body)',
                }}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </details>
  );
}
